import React from 'react';
import GuestModeManager from '../../firebase/guestModeManager';

interface RegistrationGateProps {
  type: 'publish' | 'cross-device' | 'limit-reached';
  draftTitle?: string;
  onRegister: () => void;
  onCancel?: () => void;
}

/**
 * Registration Gate Modal
 * Shows compelling reasons to register at key friction points
 */
export const RegistrationGate: React.FC<RegistrationGateProps> = ({
  type,
  draftTitle,
  onRegister,
  onCancel
}) => {
  const storageInfo = GuestModeManager.getStorageInfo();

  const getContent = () => {
    switch (type) {
      case 'publish':
        return {
          emoji: '🎉',
          title: 'Great Story!',
          subtitle: draftTitle || 'Your story is ready',
          message: (
            <>
              <p className="text-lg mb-4">
                Create a <strong>free account</strong> now to:
              </p>
              <ul className="space-y-3 text-left mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-2xl">📚</span>
                  <div>
                    <strong>Publish your story</strong>
                    <div className="text-sm text-gray-600">Share it with the Scriblish community</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">🎴</span>
                  <div>
                    <strong>Earn your first Scrib Card</strong>
                    <div className="text-sm text-gray-600">Start collecting rare writing cards</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">⚡</span>
                  <div>
                    <strong>Start your Writing Streak</strong>
                    <div className="text-sm text-gray-600">Build momentum and earn bonus XP</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">☁️</span>
                  <div>
                    <strong>Keep your stories safe</strong>
                    <div className="text-sm text-gray-600">Access from any device, anytime</div>
                  </div>
                </li>
              </ul>
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 text-sm">
                <strong>You've already earned {storageInfo.xpEarned} XP!</strong>
                <div className="text-gray-600 mt-1">
                  Sign up to claim it and unlock rewards
                </div>
              </div>
            </>
          ),
          primaryButton: 'Create Free Account',
          secondaryButton: 'Keep Editing'
        };

      case 'cross-device':
        return {
          emoji: '📱',
          title: 'Continue Your Story?',
          subtitle: 'We found a draft on this device',
          message: (
            <>
              <p className="text-lg mb-4">
                Sign up now to:
              </p>
              <ul className="space-y-3 text-left mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-2xl">☁️</span>
                  <div>
                    <strong>Transfer this draft to the cloud</strong>
                    <div className="text-sm text-gray-600">Never lose your work again</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">📱</span>
                  <div>
                    <strong>Write from anywhere</strong>
                    <div className="text-sm text-gray-600">Phone, tablet, computer - your stories sync everywhere</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">🏆</span>
                  <div>
                    <strong>Keep your streak safe</strong>
                    <div className="text-sm text-gray-600">Your progress saved forever</div>
                  </div>
                </li>
              </ul>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-sm">
                <strong>You've written {storageInfo.totalWordCount.toLocaleString()} words!</strong>
                <div className="text-gray-600 mt-1">
                  That's amazing! Sign up to make sure they're safe.
                </div>
              </div>
            </>
          ),
          primaryButton: 'Create Free Account',
          secondaryButton: 'Continue Offline'
        };

      case 'limit-reached':
        return {
          emoji: '🎯',
          title: "You're on a Roll!",
          subtitle: `${storageInfo.draftCount} drafts created`,
          message: (
            <>
              <p className="text-lg mb-4">
                You've reached the guest mode limit of <strong>{storageInfo.maxDrafts} drafts</strong>.
              </p>
              <p className="mb-6">
                Create a free account to unlock unlimited stories and keep everything you've written!
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4 mb-6">
                <div className="text-center mb-3">
                  <strong className="text-xl">Your Guest Progress</strong>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-purple-600">
                      {storageInfo.totalWordCount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Words Written</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-pink-600">
                      {storageInfo.xpEarned.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">XP Earned</div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Don't lose all this progress! Sign up now and we'll transfer everything to your account.
              </p>
            </>
          ),
          primaryButton: 'Create Free Account & Continue',
          secondaryButton: null
        };
    }
  };

  const content = getContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-3">{content.emoji}</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">
            {content.title}
          </h2>
          <p className="text-gray-600">{content.subtitle}</p>
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          {content.message}
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <button
            onClick={onRegister}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 px-6 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-lg"
          >
            {content.primaryButton}
          </button>
          
          {content.secondaryButton && onCancel && (
            <button
              onClick={onCancel}
              className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all"
            >
              {content.secondaryButton}
            </button>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Free forever • No credit card • Kid-safe
        </p>
      </div>
    </div>
  );
};

/**
 * Inline Registration Prompt
 * Subtle prompts shown during writing to encourage registration
 */
interface InlinePromptProps {
  reason: 'DRAFT_LIMIT' | 'WORD_COUNT' | 'XP_MILESTONE';
  onRegister: () => void;
  onDismiss: () => void;
}

export const InlineRegistrationPrompt: React.FC<InlinePromptProps> = ({
  reason,
  onRegister,
  onDismiss
}) => {
  const storageInfo = GuestModeManager.getStorageInfo();

  const getPromptContent = () => {
    switch (reason) {
      case 'DRAFT_LIMIT':
        return {
          icon: '📝',
          message: `You've created ${storageInfo.draftCount} drafts! Create an account to save unlimited stories.`,
          cta: 'Sign Up Free'
        };
      case 'WORD_COUNT':
        return {
          icon: '🎉',
          message: `Wow! ${storageInfo.totalWordCount.toLocaleString()} words! Sign up to claim your Writer badge.`,
          cta: 'Claim Badge'
        };
      case 'XP_MILESTONE':
        return {
          icon: '⭐',
          message: `${storageInfo.xpEarned} XP earned! Sign up to unlock your first Scrib Card.`,
          cta: 'Unlock Now'
        };
    }
  };

  const content = getPromptContent();

  return (
    <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-lg p-4 shadow-lg animate-slide-up">
      <div className="flex items-center gap-3">
        <span className="text-3xl">{content.icon}</span>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-800">{content.message}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onRegister}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-purple-600 transition-colors"
          >
            {content.cta}
          </button>
          <button
            onClick={onDismiss}
            className="text-gray-500 hover:text-gray-700 px-2"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Guest Mode Banner
 * Shows persistent indicator that user is in guest mode
 */
export const GuestModeBanner: React.FC<{ onRegister: () => void }> = ({ onRegister }) => {
  const storageInfo = GuestModeManager.getStorageInfo();
  const percentUsed = (storageInfo.draftCount / storageInfo.maxDrafts) * 100;

  return (
    <div className="bg-yellow-50 border-b-2 border-yellow-200 px-4 py-2">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <span className="text-xl">👋</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">
              Guest Mode • {storageInfo.draftCount}/{storageInfo.maxDrafts} drafts used
            </p>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1 max-w-xs">
              <div 
                className="bg-gradient-to-r from-yellow-400 to-orange-400 h-1.5 rounded-full transition-all"
                style={{ width: `${percentUsed}%` }}
              />
            </div>
          </div>
        </div>
        <button
          onClick={onRegister}
          className="bg-purple-500 text-white px-4 py-1.5 rounded-lg font-semibold text-sm hover:bg-purple-600 transition-colors whitespace-nowrap"
        >
          Sign Up Free
        </button>
      </div>
    </div>
  );
};